'use client';
import { CheckIcon } from '@heroicons/react/20/solid';
import { ArrowUpRightIcon } from '@heroicons/react/24/outline';
import { useState, useEffect, ReactNode } from 'react';
import ModalForm from '../Modal/Modal';
import React from 'react';
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import CustomRichText from '../CustomRichText';

type Feature = {
  id: string;
  name: ReactNode | SerializedEditorState;
  enters: boolean;
};

type Tier = {
  name: ReactNode | SerializedEditorState;
  id: string;
  priceSuffix: string;
  price: string;
  description: ReactNode | SerializedEditorState;
  features: Feature[];
  superPrice: boolean;
};

export interface PriceBlockUniversityProps {
  id?: string;
  title: ReactNode | SerializedEditorState;
  subtitle?: ReactNode | SerializedEditorState;
  selectPackageLabel?: string;
  showFeaturesLabel?: string;
  hideFeaturesLabel?: string;
  modalTagPrefix?: string;
  useDefaultTires?: boolean;
  tiers?: Tier[];
}

const defaultTiers: Tier[] = [
  {
    name: 'BASE',
    id: 'tier-1',
    priceSuffix: 'євро/рік',
    price: '1100',
    description: '',
    features: [
      {
        id: 'tier-1-1',
        name: (
          <>
            <p>
              <strong>Базові предмети:</strong>
            </p>
            <ul className="list-inside list-disc">
              <li>Іноземна мова (англійська та польська)</li>
              <li>Математичні та статистичні методи</li>
              <li>Мікроекономіка</li>
              <li>Основи права</li>
              <li>Управління підприємством</li>
              <li>Основи бухгалтерського обліку</li>
              <li>Підприємництво</li>
              <li>Основи фінансів</li>
              <li>Управління проєктами</li>
            </ul>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-1-2',
        name: (
          <>
            <p>Інтерактивна навчальна платформа з доступом до навчання з телефону</p>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-1-3',
        name: (
          <>
            <p>Зворотній зв&apos;язок по успішності учня та супровід протягом навчання</p>
          </>
        ),
        enters: true,
      },
    ],
    superPrice: false,
  },
  {
    name: 'BRONZE',
    id: 'tier-2',
    priceSuffix: 'євро/рік',
    price: '1630',
    description: '',
    features: [
      {
        id: 'tier-2-1',
        name: (
          <>
            <p>
              <strong>Базові предмети:</strong>
            </p>
            <ul className="list-inside list-disc">
              <li>Іноземна мова (англійська та польська)</li>
              <li>Математичні та статистичні методи</li>
              <li>Мікроекономіка</li>
              <li>Основи права</li>
              <li>Управління підприємством</li>
              <li>Основи бухгалтерського обліку</li>
              <li>Підприємництво</li>
              <li>Основи фінансів</li>
              <li>Управління проєктами</li>
            </ul>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-2-2',
        name: (
          <>
            <p>
              <strong>Вивчення іноземної мови в форматі Close to You</strong> (англ/нім/пол. на
              вибір):
            </p>
            <ul className="list-inside list-disc">
              <li>теоретичні заняття (60 хв) — граматика та словниковий запас</li>
              <li>практичні заняття (60 хв) — розвиток розмовних навичок</li>
            </ul>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-2-3',
        name: (
          <>
            <p>
              Факультативні заняття — <strong>3</strong> спеціалізовані курси:
            </p>
            <ul className="list-inside list-disc">
              <li>Фінансова грамотність</li>
              <li>СММ</li>
              <li>Таргетинг</li>
            </ul>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-2-4',
        name: (
          <>
            <p>Інтерактивна навчальна платформа з доступом до навчання з телефону</p>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-2-5',
        name: (
          <>
            <p>Зворотній зв&apos;язок по успішності учня та супровід протягом навчання</p>
          </>
        ),
        enters: true,
      },
    ],
    superPrice: false,
  },
  {
    name: 'SILVER',
    id: 'tier-3',
    price: '2050',
    priceSuffix: 'євро/рік',
    description: '',
    features: [
      {
        id: 'tier-3-1',
        name: (
          <>
            <p>
              <strong>Базові предмети:</strong>
            </p>
            <ul className="list-inside list-disc">
              <li>Іноземна мова (англійська та польська)</li>
              <li>Математичні та статистичні методи</li>
              <li>Мікроекономіка</li>
              <li>Основи права</li>
              <li>Управління підприємством</li>
              <li>Основи бухгалтерського обліку</li>
              <li>Підприємництво</li>
              <li>Основи фінансів</li>
              <li>Управління проєктами</li>
            </ul>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-3-2',
        name: (
          <>
            <p>
              <strong>Вивчення іноземної мови в форматі Close to You</strong> (англ/нім/пол. на
              вибір):
            </p>
            <ul className="list-inside list-disc">
              <li>теоретичні заняття (60 хв) — граматика та словниковий запас</li>
              <li>практичні заняття (60 хв) — розвиток розмовних навичок</li>
            </ul>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-3-3',
        name: (
          <>
            <p>
              Факультативні заняття — <strong>6</strong> спеціалізованих курсів:
            </p>
            <ul className="list-inside list-disc">
              <li>Фінансова грамотність</li>
              <li>Маркетинг PRO</li>
              <li>AI бізнес</li>
              <li>Психологія вибору</li>
              <li>Лідери професій</li>
              <li>ІТ майбутнього</li>
            </ul>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-3-4',
        name: (
          <>
            <p>Інтерактивна навчальна платформа з доступом до навчання з телефону</p>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-3-5',
        name: (
          <>
            <p>Персональна підтримка ментора</p>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-3-6',
        name: (
          <>
            <p>Підготовка до НМТ</p>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-3-7',
        name: (
          <>
            <p>Ноутбук для використання</p>
          </>
        ),
        enters: true,
      },
    ],
    superPrice: false,
  },
  {
    name: 'GOLDEN',
    id: 'tier-4',
    price: '2570',
    priceSuffix: 'євро/рік',
    description: '',
    features: [
      {
        id: 'tier-4-1',
        name: (
          <>
            <p>
              <strong>Базові предмети:</strong>
            </p>
            <ul className="list-inside list-disc">
              <li>Іноземна мова (англійська та польська)</li>
              <li>Математичні та статистичні методи</li>
              <li>Мікроекономіка</li>
              <li>Основи права</li>
              <li>Управління підприємством</li>
              <li>Основи бухгалтерського обліку</li>
              <li>Підприємництво</li>
              <li>Основи фінансів</li>
              <li>Управління проєктами</li>
            </ul>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-4-2',
        name: (
          <>
            <p>
              <strong>Вивчення іноземної мови в форматі Close to You</strong> (англ/нім/пол. на
              вибір):
            </p>
            <ul className="list-inside list-disc">
              <li>теоретичні заняття (60 хв) — граматика та словниковий запас</li>
              <li>практичні заняття (60 хв) — розвиток розмовних навичок</li>
              <li>33 індивідуальні заняття (60 хв) — робота над персональними потребами</li>
              <li>письмова гарантія на вивчення іноземної мови</li>
            </ul>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-4-3',
        name: (
          <>
            <p>
              Факультативні заняття — <strong>6</strong> спеціалізованих курсів:
            </p>
            <ul className="list-inside list-disc">
              <li>Фінансова грамотність</li>
              <li>Маркетинг PRO</li>
              <li>AI бізнес</li>
              <li>Психологія вибору</li>
              <li>Лідери професій</li>
              <li>ІТ майбутнього</li>
            </ul>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-4-4',
        name: (
          <>
            <p>Інтерактивна навчальна платформа з доступом до навчання з телефону</p>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-4-5',
        name: (
          <>
            <p>Персональна підтримка ментора + індивідуальний навчальний план розвитку</p>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-4-6',
        name: (
          <>
            <p>Підготовка до НМТ</p>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-4-7',
        name: (
          <>
            <p>Ноутбук для використання</p>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-4-8',
        name: (
          <>
            <p>
              <strong>Доступ до Success Club AP Education</strong> (лідери галузей: зустрічі з
              лідерами, будуємо кар&apos;єрний план, бізнес-тренінги, подача заяв на гранти,
              отримання стипендій)
            </p>
          </>
        ),
        enters: true,
      },
    ],
    superPrice: false,
  },
];

export default function PriceBlockUniversity({
  id = 'price',
  title = 'Вартість навчання в університеті',
  subtitle = 'Оберіть пакет, який найкраще відповідає вашим потребам',
  selectPackageLabel = 'Обрати пакет',
  showFeaturesLabel = 'Показати більше',
  hideFeaturesLabel = 'Сховати',
  modalTagPrefix = 'університет',
  useDefaultTires = true,
  tiers = defaultTiers,
}: PriceBlockUniversityProps) {
  const [dispayedTiers] = useState<Tier[]>(useDefaultTires ? defaultTiers : tiers);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<Tier | null>(null);
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  const openForm = () => setIsFormOpen(true);
  const closeForm = () => setIsFormOpen(false);

  const openModal = (tier: Tier) => {
    setSelectedTier(tier);
    setIsFormOpen(true);
  };

  const toggleFeatures = () => {
    setShowAllFeatures(prevShowAllFeatures => !prevShowAllFeatures);
  };

  useEffect(() => {
    const hashId = window.document.location.hash.slice(1);
    if (id === hashId) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [id]);

  return (
    <>
      <div id={id} data-meta="PriceBlockUniversity" className="py-14">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <CustomRichText
              data={title}
              tag="div"
              className="mt-2 text-4xl font-bold tracking-tight text-white sm:text-5xl"
            />
          </div>
          {subtitle && (
            <CustomRichText
              data={subtitle}
              tag="div"
              className="mx-auto mt-6 max-w-7xl text-center text-lg leading-8 text-white"
            />
          )}
          <div className="container mx-auto px-4 py-6 sm:py-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {dispayedTiers.map(tier => (
              <div
                key={tier.id}
                className={`rounded-3xl p-4 bg-white flex flex-col justify-between ${
                  tier.superPrice ? 'ring-2 ring-indigo-600' : 'ring-1 ring-gray-200'
                }`}
              >
                <div>
                  <div className="flex items-center justify-between gap-x-4">
                    <CustomRichText
                      id={tier.id}
                      data={tier.name}
                      tag="div"
                      className="uppercase text-white rounded-full py-1 px-4 custom-gradient-blue text-base font-semibold"
                    />
                  </div>
                  <p className="mt-6 flex items-baseline gap-x-1 border-b-2 pb-4 border-blue-200">
                    <span className="text-4xl font-bold tracking-tight text-gray-900">
                      {tier.price}
                    </span>
                    <span className="text-sm font-semibold leading-6 text-gray-900">
                      {tier.priceSuffix}
                    </span>
                  </p>
                  <span className="flex items-baseline gap-x-1 text-xs text-gray-400">
                    <CustomRichText data={tier.description} tag="span" />
                  </span>
                  <button
                    aria-describedby={tier.id}
                    onClick={() => openModal(tier)}
                    className={` relative uppercase mt-6 flex items-center justify-center w-full ring-1 ring-inset ring-blue-500 rounded-full px-1 py-2 text-sm font-semibold leading-6 ${
                      tier.superPrice ? 'bg-gray-900 text-white' : 'text-gray-900'
                    }`}
                  >
                    <span>{selectPackageLabel}</span>
                    <div className="absolute right-1 flex items-center">
                      <div className="rounded-full custom-gradient-blue-btn">
                        <ArrowUpRightIcon className="text-white h-6 w-6 m-1" />
                      </div>
                    </div>
                  </button>
                </div>
                <ul className="mt-8 space-y-3 text-sm leading-6 text-gray-600 xl:mt-10 transition-all">
                  {(showAllFeatures ? tier.features : tier.features.slice(0, 2)).map(feature => (
                    <li key={feature.id} className="flex gap-x-3">
                      <CheckIcon className="h-6 w-6 flex-none text-white custom-gradient-blue-btn rounded-full" />
                      <CustomRichText data={feature.name} tag="span" className="" />
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => toggleFeatures()}
                  className="mt-auto pt-4 text-blue-600 font-bold text-sm "
                >
                  {showAllFeatures ? hideFeaturesLabel : showFeaturesLabel}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      {isFormOpen && selectedTier && (
        <ModalForm
          closeForm={closeForm}
          btnMeta={{ tag: `${modalTagPrefix}-${selectedTier.name}` }}
          color={'blue'}
        />
      )}
    </>
  );
}
